/**
 * Shared responsive breakpoints.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The app had 130 hand-written media queries using 13 different widths (420,
 * 480, 599, 600, 720, 768, 899, 899.95, 960, 968, 1024, 1100, 1200) against a
 * single shared constant used in 4 places. The sidebar switched from rail to
 * drawer at 900px while 39 page-level rules restacked at 768px, so between
 * 768 and 900 the user got the mobile drawer with a desktop page layout, and
 * between 900 and 968 the desktop rail with a stacked mobile layout. Both of
 * those ranges are ordinary tablet widths.
 *
 * Values match MUI's defaults, since the app also uses MUI's `sx` breakpoints
 * -- having the two systems disagree is what produced the mismatch.
 *
 * The `.95` offsets matter: a `max-width: 600px` rule and MUI's
 * `min-width: 600px` both match at exactly 600px, so a viewport of precisely
 * 600 gets both the mobile and desktop rules at once.
 */

export const BREAKPOINT_VALUES = {
  /** Small phones. */
  xs: 480,
  /** Phone / tablet boundary. Matches MUI `sm`. */
  sm: 600,
  /** Rail <-> drawer switch for the sidebar. Matches MUI `md`. */
  md: 900,
  /** Matches MUI `lg`. */
  lg: 1200,
  /** Matches MUI `xl`. */
  xl: 1536,
} as const;

type Key = keyof typeof BREAKPOINT_VALUES;

const mediaDown = (k: Key) =>
  `@media (max-width:${BREAKPOINT_VALUES[k] - 0.05}px)`;

const mediaUp = (k: Key) => `@media (min-width:${BREAKPOINT_VALUES[k]}px)`;

/**
 * Usage in a JSS / sx object:
 *
 *   [BREAKPOINTS.down.md]: { flexDirection: "column" }
 */
export const BREAKPOINTS = {
  down: {
    xs: mediaDown("xs"),
    sm: mediaDown("sm"),
    md: mediaDown("md"),
    lg: mediaDown("lg"),
    xl: mediaDown("xl"),
  },
  up: {
    xs: mediaUp("xs"),
    sm: mediaUp("sm"),
    md: mediaUp("md"),
    lg: mediaUp("lg"),
    xl: mediaUp("xl"),
  },
} as const;

/** Shorthand: `import { down } from "../../styles/breakpoints"`. */
export const down = BREAKPOINTS.down;
export const up = BREAKPOINTS.up;

/**
 * The width at which the sidebar becomes an off-canvas drawer. Page layouts
 * should restack at this same width, not at a nearby one.
 */
export const MOBILE_BREAKPOINT = BREAKPOINT_VALUES.md;
